/**
 * ImportSalariesDialog.js — lista e pagave e një periudhe, e sjellë nga Excel.
 *
 * Dy hapa, jo më shumë: **Periudha & shablloni** → **Kontrollo & importo**.
 * Hapi i mesëm (ngarko skedarin) nuk ekziston si hap i vetin sepse pas
 * ngarkimit s'ka çfarë të vendosësh — skedari ose lexohet dhe kalon te kontrolli,
 * ose nuk lexohet dhe mbetesh aty ku ishe.
 *
 * ── Pse kontrolli është i detyrueshëm ────────────────────────────────────────
 * Importi shkruan paga. Një rresht i lidhur me punëtorin e gabuar, ose një neto
 * e lexuar 1.23 në vend të 1234, nuk duket kurrë si gabim te lista — duket si
 * pagë. Prandaj çdo rresht i skedarit del në ekran me punëtorin që u njoh, me
 * pagën bazë të zgjidhur dhe me neton që do të dalë, PARA se të shkruhet gjë.
 *
 * ── Të dy drejtimet ──────────────────────────────────────────────────────────
 * Kolona `Baza` e skedarit vendos rresht-për-rresht: BRUTO → neto llogaritet;
 * NETO → paga bazë zgjidhet me kërkim binar derisa neto të bjerë në qindarkë.
 * E gjithë aritmetika rri te `utils/payrollImport` + `utils/salaryCalc` — këtu
 * vendoset vetëm çfarë tregohet dhe çfarë ruhet.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, InputLabel, LinearProgress, MenuItem, Paper,
  Select, Stack, Step, StepLabel, Stepper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import UploadFileIcon      from '@mui/icons-material/UploadFile';
import DownloadIcon        from '@mui/icons-material/Download';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon    from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon     from '@mui/icons-material/ContentCopy';
import SwapVertIcon        from '@mui/icons-material/SwapVert';
import apiService from '../../services/apiServices';
import { resolveFiscal } from '../../utils/fiscalPolicy';
import { MONTHS_SQ } from '../../utils/period';
import {
  downloadImportTemplate, parseImportWorkbook, prepareImportRows, summarize,
} from '../../utils/payrollImport';

const BRAND = '#3E8FA3';
const fmt = (v) =>
  new Intl.NumberFormat('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);

const STEPS = ['Periudha & shablloni', 'Kontrollo & importo'];

const STATUS_UI = {
  ok:        { color: 'success', Icon: CheckCircleIcon,  label: 'Gati' },
  error:     { color: 'error',   Icon: ErrorOutlineIcon, label: 'Gabim' },
  duplicate: { color: 'warning', Icon: ContentCopyIcon,  label: 'Dyfishuar' },
};

/** Normat e politikës aktive vijnë si 0.05 ose si 5 — të dyja lexohen si 5%. */
const pct = (v, d) => {
  const n = Number(v);
  if (!n) return d;
  return n > 1 && n <= 100 ? n : n * 100;
};

const THIS_YEAR  = new Date().getFullYear();
const THIS_MONTH = new Date().getMonth() + 1;
/** Viti i ardhshëm hyn te lista: pagat e dhjetorit shpesh përgatiten në janar. */
const YEARS = Array.from({ length: 7 }, (_, i) => THIS_YEAR + 1 - i);

export default function ImportSalariesDialog({ open, onClose, onImported, lists = [] }) {
  const [step, setStep]   = useState(0);
  const [year, setYear]   = useState(THIS_YEAR);
  const [month, setMonth] = useState(THIS_MONTH);
  const [basis, setBasis] = useState('gross');

  const [workers, setWorkers] = useState([]);
  const [policy,  setPolicy]  = useState({});
  const [ranges,  setRanges]  = useState([]);
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [warning, setWarning] = useState('');

  const [fileName, setFileName] = useState('');
  const [rows, setRows]     = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  /* ── Konteksti fiskal ──────────────────────────────────────────────────────
   * Pa brezat tatimorë asnjë drejtim nuk llogaritet dot, ndaj ngarkohen para se
   * të hapet hapi i kontrollit — jo në momentin e ruajtjes, kur do të ishte
   * vonë për ta thënë. */
  const loadContext = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [wRes, fpeRes, fpdRes, setRes] = await Promise.all([
        apiService().get('selectworker'),
        apiService().get('fiscalPoliciesExt'),
        apiService().get('fiscalPoliticsDetails'),
        apiService().get('settings'),
      ]);
      setWorkers(Array.isArray(wRes?.data) ? wRes.data : []);
      setCompany(setRes?.data?.[0]?.CompanyName || '');

      // Politika e PERIUDHËS që po importohet — jo «e aktivja», dhe as rreshti i
      // parë i `fiscalPolitics`. Ky ishte vendi i katërt me rregull të vetin.
      const { policy: ext, ranges } = resolveFiscal({
        policies: fpeRes?.data || [],
        details:  fpdRes?.data || [],
        year, month,
      });
      setPolicy(ext || {});
      setRanges(ranges);
    } catch (e) {
      setError(e.message || 'Të dhënat bazë nuk u ngarkuan.');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    if (!open) return;
    setStep(0); setRows([]); setFileName(''); setError(''); setWarning(''); setProgress(0);
    loadContext();
  }, [open, loadContext]);

  const existingList = useMemo(
    () => lists.find((l) => Number(l.year) === Number(year) && Number(l.month) === Number(month)) || null,
    [lists, year, month],
  );

  const rates = useMemo(() => ({
    pensionEmployee:   pct(policy.PensionEmployee, 5),
    pensionEmployer:   pct(policy.PensionEmployer, 5),
    healthInsEmployee: pct(policy.HealthInsEmployee, 3.5),
    ranges,
  }), [policy, ranges]);

  /* ── Shablloni ────────────────────────────────────────────────────────────
   * Parambushet me punëtorët aktivë dhe me pagën e tyre bazë nga kontrata: një
   * fletë bosh do ta detyronte HR-në të rishkruante emrat që sistemi i ka. */
  const downloadTemplate = () => {
    downloadImportTemplate({
      year, month, basis, company, rates,
      workers: workers.map((w) => ({
        nid:       w.PersonalNumber || w.personal_number || '',
        firstName: w.FirstName || '',
        lastName:  w.LastName || '',
        amount:    Number(w.BaseSalary) || 0,
        healthPersonal: Number(w.HealthInsPersonal) || 0,
        healthFamily:   Number(w.HealthInsFamily)   || 0,
      })),
    });
  };

  /* ── Leximi i skedarit ─────────────────────────────────────────────────── */
  const readFile = async (file) => {
    if (!file) return;
    setError(''); setWarning(''); setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const parsed = parseImportWorkbook(new Uint8Array(buf));
      if (!parsed.ok) { setError(parsed.error); setRows([]); return; }
      if (!parsed.rows.length) { setError('Skedari nuk ka asnjë rresht me punëtor.'); setRows([]); return; }

      // Periudha e skedarit është vendimtare, jo dekorative: një shabllon i një
      // muaji tjetër do të shkruante paga te periudha e gabuar.
      if (parsed.year && parsed.month &&
          (Number(parsed.year) !== Number(year) || Number(parsed.month) !== Number(month))) {
        setError(`Skedari është për ${MONTHS_SQ[parsed.month - 1]} ${parsed.year}, ndërsa periudha e zgjedhur është ${MONTHS_SQ[month - 1]} ${year}. Ndrysho periudhën ose përdor skedarin e duhur.`);
        setRows([]); return;
      }
      if (!parsed.year || !parsed.month) {
        setWarning('Skedari nuk e mbante periudhën brenda — u morën viti dhe muaji i zgjedhur në ekran.');
      }

      const prepared = prepareImportRows(parsed, { workers, policy, ranges });
      setRows(prepared);
      setStep(1);
    } catch (e) {
      setError(e.message || 'Skedari nuk u lexua dot.');
      setRows([]);
    }
  };

  const onPick = (e) => { readFile(e.target.files?.[0]); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); readFile(e.dataTransfer?.files?.[0]); };

  const sum = useMemo(() => summarize(rows), [rows]);

  /* ── Ruajtja ──────────────────────────────────────────────────────────────
   * E njëjta rrugë si ekrani i pagave: `computeSalary` te serveri, pastaj
   * `bulk-save`. Rreshtat e punëtorëve që ndodhen tashmë te lista përditësohen
   * në vend që të dyfishohen — importi i dytë i së njëjtës periudhë duhet të
   * korrigjojë, jo të shtojë. */
  const runImport = async () => {
    const ready = rows.filter((r) => r.status === 'ok');
    if (!ready.length) return;
    setSaving(true); setError(''); setProgress(10);
    try {
      let listId = existingList?.id ?? existingList?.Id ?? null;
      if (!listId) {
        const check = await apiService().query('checkList', `month=${month}&year=${year}`);
        listId = check?.data?.[0]?.id ?? check?.data?.[0]?.Id ?? null;
      }
      if (!listId) {
        const res = await apiService().post('salariesmonthlylists', {
          month, year, name: `${MONTHS_SQ[month - 1]} ${year}`, createdBy: 'Import',
        });
        const rec = Array.isArray(res?.data) ? res.data[0] : res?.data;
        listId = rec?.id ?? rec?.Id ?? null;
        if (!listId) throw new Error('Lista u krijua por serveri nuk ktheu ID.');
      }
      setProgress(35);

      const existingRes = await apiService().query('getSalariesByListId', `id=${listId}`);
      const existingByWorker = new Map(
        (existingRes?.data || []).map((r) => [Number(r.WorkerId ?? r.workerId), r]),
      );
      setProgress(50);

      const entities = ready.map((r) => ({
        listId, month,
        WorkerId:   r.workerId,
        Salary:     r.base,
        HourSalary: r.overtime,
        Bonus:      r.bonus,
        Advances:   r.advances,
        Penalties:  r.penalties,
        ExtraHours: 0, ExtraHoursWeekend: 0, ExtraHoursHoliday: 0,
      }));

      // Llogaritja përfundimtare bëhet te serveri, si te ruajtja e listës —
      // ekrani tregoi parashikimin, baza ruan atë që llogarit backend-i.
      let computed = entities;
      try {
        const c = await apiService().execute('computeSalary', { rows: entities });
        if (c?.success && c.data?.length === entities.length) {
          computed = entities.map((e, i) => ({ ...e, ...c.data[i] }));
        }
      } catch { /* pa backend-in, ruhen vlerat e ekranit */ }
      setProgress(70);

      const objs = computed.map((entity) => {
        const prev = existingByWorker.get(Number(entity.WorkerId));
        return prev
          ? { type: 'salaries', status: 2, entity: { ...entity, id: prev.id ?? prev.Id } }
          : { type: 'salaries', status: 1, id: 0, entity };
      });

      const saved = await apiService().saveChanges(objs);
      setProgress(100);
      if (!saved?.success) {
        const first = (saved?.data || []).find((r) => r?.success === false)?.error;
        throw new Error(first ? `Ruajtja dështoi: ${first}` : 'Ruajtja dështoi.');
      }
      const created = objs.filter((o) => o.status === 1).length;
      onImported?.({
        listId,
        message: `U importuan ${ready.length} rreshta për ${MONTHS_SQ[month - 1]} ${year} — ${created} të reja, ${ready.length - created} të përditësuara.`,
      });
      onClose?.();
    } catch (e) {
      setError(e.message || 'Importi dështoi.');
    } finally {
      setSaving(false);
    }
  };

  const fiscalMissing = !loading && !ranges.length;

  return (
    <Dialog open={open} onClose={() => !saving && onClose?.()} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(BRAND, 0.1),
                     display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <UploadFileIcon sx={{ color: BRAND, fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.02rem', lineHeight: 1.2 }}>
              Importo listën e pagave
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Nga Excel — bruto → neto dhe neto → bruto, sipas vitit dhe periudhës
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Stepper activeStep={step} sx={{ display: { xs: 'none', sm: 'flex' }, minWidth: 300,
                    '& .MuiStepLabel-label': { fontSize: '0.74rem' } }}>
            {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>
        </Stack>
      </DialogTitle>
      <Divider />

      {(loading || saving) && <LinearProgress variant={saving ? 'determinate' : 'indeterminate'} value={progress} />}

      <DialogContent sx={{ p: 2.5 }}>
        {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {warning && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setWarning('')}>{warning}</Alert>}
        {fiscalMissing && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Nuk ka politikë fiskale aktive me breza tatimorë — llogaritja do të bjerë te normat e parazgjedhura.
          </Alert>
        )}

        {/* ══ Hapi 1 — periudha, baza, shablloni ═══════════════════════════ */}
        {step === 0 && (
          <Stack spacing={2}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.06em',
                                color: 'text.secondary', textTransform: 'uppercase', mb: 1.5 }}>
                Periudha e listës
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Viti</InputLabel>
                  <Select label="Viti" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Periudha</InputLabel>
                  <Select label="Periudha" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                    {MONTHS_SQ.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
                <Box sx={{ flex: 1 }} />
                <Chip size="small"
                  color={existingList ? 'warning' : 'default'}
                  variant={existingList ? 'filled' : 'outlined'}
                  label={existingList
                    ? (existingList.status === 'closed' ? 'Lista ekziston — e procesuar' : 'Lista ekziston — do të përditësohet')
                    : 'Lista do të krijohet'}
                  sx={{ fontWeight: 600, fontSize: '0.72rem' }} />
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.06em',
                                color: 'text.secondary', textTransform: 'uppercase', mb: 1.5 }}>
                Baza e paracaktuar e shabllonit
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <ToggleButtonGroup size="small" exclusive value={basis}
                  onChange={(_, v) => v && setBasis(v)}
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, fontWeight: 700, fontSize: '0.8rem' } }}>
                  <ToggleButton value="gross">BRUTO → neto</ToggleButton>
                  <ToggleButton value="net">NETO → bruto</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {basis === 'net'
                    ? 'Shuma e çdo rreshti lexohet si paga në dorë; paga bruto zgjidhet prej saj.'
                    : 'Shuma e çdo rreshti lexohet si pagë bazë; neto llogaritet prej saj.'}
                  {' '}Kolona <strong>Baza</strong> te skedari e mbivendos këtë rresht për rresht.
                </Typography>
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button fullWidth variant="outlined" startIcon={<DownloadIcon />}
                onClick={downloadTemplate} disabled={loading || !workers.length}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.2 }}>
                Shkarko shabllonin ({workers.length} punëtorë)
              </Button>
              <Button fullWidth variant="contained" disableElevation startIcon={<UploadFileIcon />}
                onClick={() => fileRef.current?.click()} disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.2 }}>
                Ngarko skedarin e plotësuar
              </Button>
            </Stack>

            <Box onDrop={loading ? undefined : onDrop} onDragOver={(e) => e.preventDefault()}
              onClick={() => !loading && fileRef.current?.click()}
              sx={{ p: 2.5, textAlign: 'center', borderRadius: 2,
                    cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.5 : 1,
                    border: '1.5px dashed', borderColor: 'divider', bgcolor: '#F8FAFC',
                    '&:hover': loading ? {} : { borderColor: BRAND, bgcolor: alpha(BRAND, 0.04) } }}>
              <SwapVertIcon sx={{ color: 'text.disabled', fontSize: 26 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Lësho këtu skedarin <strong>.xlsx</strong> — ose kliko për ta zgjedhur
              </Typography>
              {fileName && <Typography variant="caption" color="text.disabled">{fileName}</Typography>}
            </Box>

            <input ref={fileRef} type="file" hidden accept=".xlsx,.xls" onChange={onPick} />
          </Stack>
        )}

        {/* ══ Hapi 2 — kontrolli ═══════════════════════════════════════════ */}
        {step === 1 && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
              {[
                { l: `${sum.ready} gati`,      c: '#16A34A', b: '#ECFDF5', show: true },
                { l: `${sum.errors} gabime`,   c: '#DC2626', b: '#FEF2F2', show: sum.errors > 0 },
                { l: `${sum.duplicates} dyfish`, c: '#D97706', b: '#FFFBEB', show: sum.duplicates > 0 },
                { l: `${sum.grossRows} bruto → neto`, c: '#2563EB', b: '#EFF6FF', show: sum.grossRows > 0 },
                { l: `${sum.netRows} neto → bruto`,   c: '#9333EA', b: '#F5F3FF', show: sum.netRows > 0 },
              ].filter((s) => s.show).map((s) => (
                <Chip key={s.l} size="small" label={s.l}
                  sx={{ bgcolor: s.b, color: s.c, fontWeight: 700, fontSize: '0.7rem', height: 22, border: 'none' }} />
              ))}
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                Bazë <strong>{fmt(sum.base)} €</strong> · Bruto <strong>{fmt(sum.gross)} €</strong> · Neto <strong>{fmt(sum.net)} €</strong>
              </Typography>
            </Stack>

            {existingList?.status === 'closed' && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Kjo listë është shënuar si e procesuar. Importi do ta ndryshojë — çdo ndryshim mbetet te gjurma e auditimit.
              </Alert>
            )}

            <TableContainer component={Paper} elevation={0}
              sx={{ maxHeight: 380, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Punëtori', 'Baza', 'Shuma', 'Paga bazë', 'Tatimi', 'Neto', ''].map((h, i) => (
                      <TableCell key={h + i} align={i > 1 && i < 6 ? 'right' : 'left'}
                        sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#F8FAFC', py: 0.75 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => {
                    const ui = STATUS_UI[r.status] || STATUS_UI.error;
                    const bad = r.status !== 'ok';
                    return (
                      <TableRow key={`${r.excelRow}-${i}`} hover
                        sx={{ bgcolor: bad ? alpha('#DC2626', r.status === 'duplicate' ? 0.03 : 0.045) : undefined }}>
                        <TableCell sx={{ py: 0.6 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.25 }}>
                            {r.workerName || r.fullName || '—'}
                          </Typography>
                          <Typography variant="caption" color={bad ? 'error.main' : 'text.disabled'}>
                            {bad ? r.error : `Rreshti ${r.excelRow}${r.matchedBy === 'name' ? ' · njohur nga emri' : ''}`}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.6 }}>
                          <Chip size="small" label={r.basis === 'net' ? 'NETO' : 'BRUTO'}
                            sx={{ height: 19, fontSize: '0.63rem', fontWeight: 700,
                                  bgcolor: r.basis === 'net' ? '#F5F3FF' : '#EFF6FF',
                                  color:   r.basis === 'net' ? '#9333EA' : '#2563EB' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: '0.8rem' }}>{fmt(r.amount)}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: '0.8rem', fontWeight: 700 }}>
                          {r.base != null ? fmt(r.base) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: '0.8rem', color: 'text.secondary' }}>
                          {r.totals ? fmt(r.totals.tax) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: '0.8rem', fontWeight: 700, color: '#16A34A' }}>
                          {r.totals ? fmt(r.totals.net) : '—'}
                        </TableCell>
                        <TableCell sx={{ py: 0.6, width: 34 }}>
                          <Tooltip title={r.drift ? `Neto e arritur ndryshon ${fmt(r.drift)} € nga e kërkuara` : ui.label}>
                            <ui.Icon sx={{ fontSize: 17, color: `${ui.color}.main` }} />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary">
              Do të shkruhen vetëm rreshtat <strong>gati</strong>. Rreshtat me gabim nuk ndalojnë importin — korrigjoji te skedari dhe ngarkoje sërish.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        {step === 1 && (
          <Button onClick={() => { setStep(0); setRows([]); }} disabled={saving}
            sx={{ textTransform: 'none' }}>Prapa</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => onClose?.()} disabled={saving} sx={{ textTransform: 'none' }}>Anulo</Button>
        {step === 1 && (
          <Button variant="contained" disableElevation onClick={runImport}
            disabled={saving || !sum.ready}
            startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <CheckCircleIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
            {saving ? 'Duke importuar…' : `Importo ${sum.ready} rreshta`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
